/* "Pick your +1" strip in the cart drawer.
 *
 * Two sources, picked server-side in theme.liquid:
 *   - data-source="manual": the products chosen in theme settings, rendered
 *     straight into this element. Nothing is fetched.
 *   - otherwise: Shopify product recommendations, fetched for the first cart item.
 *
 * For both, a product that is already in the cart is never shown: cards are
 * hidden (and shown again if the line is removed) every time the cart changes.
 */
class CartRecommendations extends HTMLElement {
  constructor() {
    super();

    this.manual = this.dataset.source === 'manual';
    // component-cart.js blanks innerHTML before regenerating, so keep a copy of
    // the server-rendered list to restore.
    this.manualHTML = this.manual ? this.innerHTML : '';

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('scroll', () => this.updateNav(), true);
    if (typeof ResizeObserver === 'function') {
      // The drawer is display:none until opened, so arrow state can only be
      // measured once the strip actually has a size.
      new ResizeObserver(() => this.updateNav()).observe(this);
    }

    // The drawer HTML is repainted on every cart change; re-check the cards then.
    const cart = document.getElementById('AjaxCartForm');
    if (cart) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.syncCart();
        });
      }).observe(cart, { childList: true, subtree: true });
    }

    this.generateRecommendations();
  }

  generateRecommendations() {
    if (this.manual) {
      if (!this.innerHTML.trim()) this.innerHTML = this.manualHTML;
      this.syncCart();
      return;
    }

    const cartItems = document.getElementById('AjaxCartForm').querySelectorAll('[data-js-cart-item]');
    if (cartItems.length > 0) {
      fetch(
        `${KROWN.settings.routes.product_recommendations_url}?section_id=${this.dataset.section}&product_id=${cartItems[0].dataset.productId}&limit=${this.dataset.limit}`
      )
        .then((response) => response.text())
        .then((text) => {
          const innerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('[data-js-cart-recommendations-performed]');
          if (innerHTML && innerHTML.querySelectorAll('[data-js-cart-rec-item]').length > 0) {
            this.innerHTML = innerHTML.innerHTML;
            this.syncCart();
          }
        });
    }
  }

  // Hide products that are already in the cart (and show them again once the
  // line is removed), and the whole strip when nothing is left.
  syncCart() {
    const inCart = new Set(
      Array.from(document.querySelectorAll('#AjaxCartForm [data-js-cart-item]')).map((el) => el.dataset.productId)
    );
    let visible = 0;
    this.querySelectorAll('[data-js-cart-rec-item]').forEach((el) => {
      const hide = inCart.has(el.dataset.productId);
      el.hidden = hide;
      if (!hide) visible++;
    });
    this.hidden = visible === 0;
    this.updateNav();
  }

  onClick(e) {
    const add = e.target.closest('[data-js-cart-rec-add]');
    if (add) {
      this.quickAdd(add);
      return;
    }
    if (e.target.closest('[data-js-cart-rec-prev]')) this.scrollTrack(-1);
    if (e.target.closest('[data-js-cart-rec-next]')) this.scrollTrack(1);
  }

  quickAdd(button) {
    if (button.classList.contains('is-working') || typeof window.handleAddToCart !== 'function') return;

    button.classList.add('is-working');
    button.setAttribute('aria-busy', 'true');

    // No variant picker: the button carries the product's first available variant.
    const variantId = button.dataset.variantId;

    window.handleAddToCart(
      `id=${encodeURIComponent(variantId)}&quantity=1`,
      1,
      {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      () => {
        // The drawer repaint may have replaced this card already.
        button.classList.remove('is-working');
        button.removeAttribute('aria-busy');
      }
    );
  }

  scrollTrack(direction) {
    const track = this.querySelector('[data-js-cart-rec-track]');
    const item = track && track.querySelector('[data-js-cart-rec-item]:not([hidden])');
    if (!item) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  updateNav() {
    const track = this.querySelector('[data-js-cart-rec-track]');
    const prev = this.querySelector('[data-js-cart-rec-prev]');
    const next = this.querySelector('[data-js-cart-rec-next]');
    if (!track || !prev || !next || !track.clientWidth) return;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
  }
}

if (typeof customElements.get('cart-recommendations') === 'undefined') {
  customElements.define('cart-recommendations', CartRecommendations);
}
